// This module is also bundled into the blocking head script. Keep it browser-only
// and independent of React/auth so restoration uses the same validation and palette.
export const themeStoragePrefix = "orbit:theme:v1:"
export const activeThemeUserKey = `${themeStoragePrefix}active-user`
export const defaultThemePreference = {
	version: 1 as const,
	theme: "dark" as "dark" | "light" | "custom",
	background: "#25262a",
	accent: "#e83c60"
}

export function parseThemePreference(value: unknown) {
	if (
		!value ||
		typeof value !== "object" ||
		!("version" in value) ||
		value.version !== 1 ||
		!("theme" in value) ||
		(value.theme !== "dark" &&
			value.theme !== "light" &&
			value.theme !== "custom") ||
		!("background" in value) ||
		typeof value.background !== "string" ||
		!/^#[\da-f]{6}$/i.test(value.background) ||
		!("accent" in value) ||
		typeof value.accent !== "string" ||
		!/^#[\da-f]{6}$/i.test(value.accent)
	)
		return { ...defaultThemePreference }
	return {
		version: 1,
		theme: value.theme,
		background: value.background.toLowerCase(),
		accent: value.accent.toLowerCase()
	} satisfies typeof defaultThemePreference
}

export function readActiveThemeUser() {
	try {
		return localStorage.getItem(activeThemeUserKey)
	} catch {
		return null
	}
}

export function readThemePreference(userId: string | null) {
	try {
		return parseThemePreference(
			userId
				? JSON.parse(
						localStorage.getItem(`${themeStoragePrefix}${userId}`) ?? "null"
					)
				: null
		)
	} catch {
		return { ...defaultThemePreference }
	}
}

export function cacheThemePreference(
	userId: string,
	preference: ReturnType<typeof parseThemePreference>
) {
	try {
		localStorage.setItem(
			`${themeStoragePrefix}${userId}`,
			JSON.stringify(preference)
		)
		localStorage.setItem(activeThemeUserKey, userId)
	} catch {
		// The account remains authoritative when browser storage is unavailable.
	}
}

export function relativeLuminance(hex: string) {
	const channels = [1, 3, 5].map((offset) => {
		const channel = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255
		return channel <= 0.04045
			? channel / 12.92
			: ((channel + 0.055) / 1.055) ** 2.4
	})
	return channels[0]! * 0.2126 + channels[1]! * 0.7152 + channels[2]! * 0.0722
}

export function contrastRatio(a: string, b: string) {
	const first = relativeLuminance(a)
	const second = relativeLuminance(b)
	return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

function mixColor(from: string, to: string, amount: number) {
	return `#${[1, 3, 5]
		.map((offset) => {
			const start = Number.parseInt(from.slice(offset, offset + 2), 16)
			const end = Number.parseInt(to.slice(offset, offset + 2), 16)
			return Math.round(start + (end - start) * amount)
				.toString(16)
				.padStart(2, "0")
		})
		.join("")}`
}

function readableColor(
	color: string,
	surfaces: string[],
	target: string,
	ratio = 4.5
) {
	for (let step = 0; step <= 100; step++) {
		const candidate = mixColor(color, target, step / 100)
		if (surfaces.every((surface) => contrastRatio(candidate, surface) >= ratio))
			return candidate
	}
	return target
}

export function customThemeTokens(background: string, accent: string) {
	const dark = relativeLuminance(background) < 0.179
	const foreground = dark ? "#ffffff" : "#000000"
	// Near mid-luminance, lift surfaces toward the background's pole instead of
	// crossing the contrast boundary. The selected base colour is always retained.
	const surfacePole = dark
		? relativeLuminance(background) < 0.08
			? "#ffffff"
			: "#000000"
		: relativeLuminance(background) > 0.45
			? "#000000"
			: "#ffffff"
	const surfaces = [0, 0.04, 0.08, 0.14, 0.22].map((amount) => {
		const surface = mixColor(background, surfacePole, amount)
		return readableColor(surface, [foreground], background)
	})
	const primaryForeground =
		contrastRatio(accent, "#ffffff") > contrastRatio(accent, "#000000")
			? "#ffffff"
			: "#000000"
	const mentionBackground = mixColor(background, accent, 0.18)
	return {
		"--background": background,
		"--color-background-tertiary": surfaces[1]!,
		"--color-background-secondary": surfaces[2]!,
		"--color-background-elevated": surfaces[3]!,
		"--color-active": surfaces[4]!,
		"--color-text-main": readableColor(
			mixColor(background, foreground, 0.95),
			surfaces,
			foreground
		),
		"--color-text-sub": readableColor(
			mixColor(background, foreground, 0.78),
			surfaces,
			foreground
		),
		"--color-text-small": readableColor(
			mixColor(background, foreground, 0.62),
			surfaces,
			foreground
		),
		"--color-icons": readableColor(
			mixColor(background, foreground, 0.72),
			surfaces,
			foreground
		),
		"--color-stroke": readableColor(background, surfaces, foreground, 3),
		"--color-stroke-hover": readableColor(
			background,
			surfaces,
			foreground,
			4.5
		),
		"--color-button-edge": mixColor(background, "#000000", dark ? 0.45 : 0.18),
		"--color-orbit": accent,
		"--color-orbit-text": readableColor(accent, surfaces, foreground),
		"--color-mention": mentionBackground,
		"--color-mention-foreground": readableColor(
			accent,
			[mentionBackground],
			relativeLuminance(mentionBackground) < 0.179 ? "#ffffff" : "#000000"
		),
		"--primary-foreground": primaryForeground,
		"--primary-hover": mixColor(
			accent,
			primaryForeground === "#ffffff" ? "#000000" : "#ffffff",
			0.1
		),
		"--ring": readableColor(accent, surfaces, foreground, 3)
	}
}

export function applyTheme(
	preference: ReturnType<typeof parseThemePreference>
) {
	const root = document.documentElement
	// Remove only the properties owned by this theme; native safe-area tokens stay.
	for (const token of Object.keys(
		customThemeTokens(
			defaultThemePreference.background,
			defaultThemePreference.accent
		)
	))
		root.style.removeProperty(token)
	if (preference.theme === "custom") {
		for (const [token, value] of Object.entries(
			customThemeTokens(preference.background, preference.accent)
		))
			root.style.setProperty(token, value)
	}
	const colorScheme =
		preference.theme === "light" ||
		(preference.theme === "custom" &&
			relativeLuminance(preference.background) >= 0.179)
			? "light"
			: "dark"
	root.dataset.theme = preference.theme
	root.dataset.colorScheme = colorScheme
	// Existing Dark never used a root .dark class. Keep its component variants
	// unchanged; the editor receives its own scheme through ThemeProvider.
	root.style.colorScheme = colorScheme
	return colorScheme
}
